import { z } from 'zod';
import { IsoDateTime } from './types/common.js';

/**
 * CDS findings follow the shape of HL7 CDS Hooks cards so the native rule engine and a future
 * CQF Clinical Reasoning / CDS Hooks service can be swapped behind the same contract.
 */
export const CdsSeveritySchema = z.enum(['info', 'warning', 'critical']);
export type CdsSeverity = z.infer<typeof CdsSeveritySchema>;

export const CdsFindingSchema = z.object({
  findingId: z.string(),
  ruleId: z.string().describe('Stable rule identifier, e.g. core-safety/drug-allergy'),
  rulePack: z.string(),
  severity: CdsSeveritySchema,
  summary: z.string().max(140),
  detail: z.string().optional(),
  /** Knowledge source and its validation status. Shown to the clinician verbatim. */
  source: z.object({
    label: z.string(),
    url: z.string().url().optional(),
    validated: z.boolean().describe('false until the knowledge source has been clinically validated for this tenant'),
  }),
  /** Critical findings require explicit acknowledgement before confirmation; none are silently blocking. */
  requiresAcknowledgement: z.boolean().default(false),
  /** Which draft items triggered the finding (order item index, medication code, etc.). */
  relatedItems: z.array(z.string()).default([]),
  suggestions: z.array(z.object({ label: z.string(), action: z.string().optional() })).default([]),
  evaluatedAt: IsoDateTime,
});
export type CdsFinding = z.infer<typeof CdsFindingSchema>;

export const CdsHookSchema = z.enum(['order-sign', 'medication-prescribe', 'patient-view', 'encounter-start']);
export type CdsHook = z.infer<typeof CdsHookSchema>;

export const CdsEvaluationSchema = z.object({
  hook: CdsHookSchema,
  patientId: z.string(),
  findings: z.array(CdsFindingSchema),
  rulePacksEvaluated: z.array(z.string()),
  engine: z.string().describe('e.g. ampshift-native-rules@0.1 or cqf-clinical-reasoning@3.x'),
  evaluatedAt: IsoDateTime,
  durationMs: z.number().nonnegative(),
});
export type CdsEvaluation = z.infer<typeof CdsEvaluationSchema>;
